import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Clock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ShiftModal = ({ open, onClose, date, shift, workers, shiftTypes, onSuccess }) => {
  const [formData, setFormData] = useState({
    worker_id: "",
    shift_type_id: "",
    notes: "",
    custom_start_time: "",
    custom_end_time: ""
  });
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (shift) {
      setFormData({
        worker_id: shift.worker_id,
        shift_type_id: shift.shift_type_id,
        notes: shift.notes || "",
        custom_start_time: shift.custom_start_time || "",
        custom_end_time: shift.custom_end_time || ""
      });
      setUseCustomTime(!!(shift.custom_start_time && shift.custom_end_time));
    } else {
      setFormData({ worker_id: "", shift_type_id: "", notes: "", custom_start_time: "", custom_end_time: "" });
      setUseCustomTime(false);
    }
  }, [shift]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const submitData = {
      worker_id: formData.worker_id,
      shift_type_id: formData.shift_type_id,
      notes: formData.notes,
      custom_start_time: useCustomTime ? formData.custom_start_time : null,
      custom_end_time: useCustomTime ? formData.custom_end_time : null
    };

    try {
      if (shift) {
        await axios.put(`${API}/shifts/${shift.id}`, submitData, { headers });
        toast.success("Schicht aktualisiert!");
      } else {
        await axios.post(`${API}/shifts`, { ...submitData, date }, { headers });
        toast.success("Schicht erstellt!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern der Schicht");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/shifts/${shift.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Schicht gelöscht!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Fehler beim Löschen der Schicht");
    }
    setConfirmDelete(false);
  };

  const selectedShiftType = shiftTypes.find(st => st.id === formData.shift_type_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="shift-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {shift ? "Schicht bearbeiten" : "Schicht hinzufügen"}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Datum</Label>
            <div className="mt-1 px-3 py-2 bg-slate-50 rounded-md text-sm text-slate-700" data-testid="shift-date">
              {new Date(date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>

          <div>
            <Label htmlFor="worker">Mitarbeiter *</Label>
            <Select
              value={formData.worker_id}
              onValueChange={(value) => setFormData({ ...formData, worker_id: value })}
              required
            >
              <SelectTrigger id="worker" data-testid="shift-worker-select">
                <SelectValue placeholder="Mitarbeiter auswählen" />
              </SelectTrigger>
              <SelectContent>
                {workers.map(worker => (
                  <SelectItem key={worker.id} value={worker.id} data-testid={`worker-option-${worker.id}`}>
                    {worker.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="shift-type">Schichttyp *</Label>
            <Select
              value={formData.shift_type_id}
              onValueChange={(value) => setFormData({ ...formData, shift_type_id: value })}
              required
            >
              <SelectTrigger id="shift-type" data-testid="shift-type-select">
                <SelectValue placeholder="Schichttyp auswählen" />
              </SelectTrigger>
              <SelectContent>
                {shiftTypes.map(type => (
                  <SelectItem key={type.id} value={type.id} data-testid={`shift-type-option-${type.id}`}>
                    {type.name} ({type.start_time} - {type.end_time})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center space-x-2 mb-3">
              <Checkbox 
                id="custom-time" 
                checked={useCustomTime}
                onCheckedChange={setUseCustomTime}
                data-testid="custom-time-checkbox"
              />
              <label htmlFor="custom-time" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Benutzerdefinierte Zeiten für diesen Tag
              </label>
            </div>

            {useCustomTime && (
              <div className="grid grid-cols-2 gap-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                <div>
                  <Label htmlFor="custom-start" className="text-xs">Startzeit</Label>
                  <Input
                    id="custom-start"
                    type="time"
                    value={formData.custom_start_time}
                    onChange={(e) => setFormData({ ...formData, custom_start_time: e.target.value })}
                    required={useCustomTime}
                    data-testid="custom-start-time"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="custom-end" className="text-xs">Endzeit</Label>
                  <Input
                    id="custom-end"
                    type="time"
                    value={formData.custom_end_time}
                    onChange={(e) => setFormData({ ...formData, custom_end_time: e.target.value })}
                    required={useCustomTime}
                    data-testid="custom-end-time"
                    className="mt-1"
                  />
                </div>
                {selectedShiftType && (
                  <div className="col-span-2 text-xs text-slate-600">
                    Standard: {selectedShiftType.start_time} - {selectedShiftType.end_time}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional: Notizen hinzufügen..."
              data-testid="shift-notes-textarea"
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-between pt-2">
            {shift && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-shift-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {confirmDelete ? "Bestätigen?" : "Löschen"}
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-shift-button">
                Abbrechen
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="save-shift-button">
                {loading ? "Speichern..." : shift ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftModal;